import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Edit, Users, Trash2, AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { PacienteEditModal } from "./PacienteEditModal";
import { useSubscriptionStore } from "@/stores/subscriptionStore";

interface Patient {
  id: string;
  full_name: string;
  cpf: string | null;
  email: string | null;
  data_nascimento: string | null;
  status: string;
}

interface PacientesListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profissionalId: string;
  profissionalName: string;
}

export function PacientesListModal({
  open,
  onOpenChange,
  profissionalId,
  profissionalName
}: PacientesListModalProps) {
  const refreshPatientsCount = useSubscriptionStore((state) => state.refreshPatientsCount);
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [editingPatient, setEditingPatient] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");

  useEffect(() => {
    if (open && profissionalId) {
      loadPatients();
    }
  }, [open, profissionalId]);

  const loadPatients = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("patients_onboarding")
        .select("id, full_name, cpf, email, data_nascimento, status")
        .eq("owner_id", profissionalId)
        .order("full_name");

      if (error) throw error;
      setPatients(data || []);
      setSelectedIds(new Set()); // Limpar seleção ao recarregar
    } catch (error) {
      console.error("Erro ao carregar pacientes:", error);
      toast.error("Erro ao carregar pacientes");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(patients.map(p => p.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectPatient = (patientId: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(patientId);
    } else {
      newSelected.delete(patientId);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    try {
      setDeleting(true);

      const { data, error } = await supabase.functions.invoke(
        'delete-patient-cascade',
        { body: { patientIds: Array.from(selectedIds) } }
      );

      if (error) throw error;

      toast.success(
        <div>
          <strong>✅ {selectedIds.size} pacientes excluídos com sucesso!</strong>
          {data?.stats && (
            <ul className="list-disc list-inside mt-2 text-sm">
              <li>{data.stats.documents} documentos removidos</li>
              <li>{data.stats.lab_results} resultados de exames deletados</li>
              <li>{data.stats.files} arquivos removidos do storage</li>
            </ul>
          )}
        </div>
      );

      await loadPatients();
      // Atualizar contagem de pacientes no store global
      await refreshPatientsCount();
      setBulkDeleting(false);
      setConfirmationText('');

    } catch (error) {
      console.error('Erro ao excluir pacientes:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao excluir pacientes');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Pacientes de {profissionalName}
                </DialogTitle>
                <DialogDescription>
                  {patients.length} paciente{patients.length !== 1 ? 's' : ''} vinculado{patients.length !== 1 ? 's' : ''}
                  {selectedIds.size > 0 && (
                    <span className="ml-2 text-primary font-medium">
                      · {selectedIds.size} selecionado{selectedIds.size !== 1 ? 's' : ''}
                    </span>
                  )}
                </DialogDescription>
              </div>
              {selectedIds.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleting(true)}
                  className="ml-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir ({selectedIds.size})
                </Button>
              )}
            </div>
          </DialogHeader>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : patients.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>Nenhum paciente cadastrado</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]">
                      <Checkbox
                        checked={selectedIds.size === patients.length && patients.length > 0}
                        onCheckedChange={handleSelectAll}
                        aria-label="Selecionar todos"
                      />
                    </TableHead>
                    <TableHead>Nome</TableHead>
                    <TableHead>CPF</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Nascimento</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {patients.map((patient) => (
                    <TableRow key={patient.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(patient.id)}
                          onCheckedChange={(checked) => handleSelectPatient(patient.id, checked as boolean)}
                          aria-label={`Selecionar ${patient.full_name}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{patient.full_name}</TableCell>
                      <TableCell>{patient.cpf || '-'}</TableCell>
                      <TableCell>{patient.email || '-'}</TableCell>
                      <TableCell>
                        {patient.data_nascimento
                          ? format(new Date(patient.data_nascimento), 'dd/MM/yyyy', { locale: ptBR })
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Badge variant={patient.status === 'active' ? 'default' : 'secondary'}>
                          {patient.status === 'active' ? 'Ativo' : 'Inativo'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditingPatient(patient.id)}
                          title="Editar paciente"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {editingPatient && (
        <PacienteEditModal
          open={!!editingPatient}
          onOpenChange={(open) => !open && setEditingPatient(null)}
          patientId={editingPatient}
          onSuccess={() => {
            loadPatients();
            setEditingPatient(null);
          }}
        />
      )}

      <AlertDialog open={bulkDeleting} onOpenChange={setBulkDeleting}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Excluir {selectedIds.size} Paciente{selectedIds.size !== 1 ? 's' : ''}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <Alert variant="destructive">
                <AlertDescription>
                  <strong>ATENÇÃO:</strong> Esta ação é permanente e não pode ser desfeita.
                  Serão excluídos:
                  <ul className="list-disc list-inside mt-2 text-sm">
                    <li>{selectedIds.size} paciente{selectedIds.size !== 1 ? 's' : ''}</li>
                    <li>Todos os documentos dos pacientes</li>
                    <li>Todos os resultados de exames</li>
                    <li>Todos os registros de saúde</li>
                    <li>Todos os arquivos relacionados</li>
                  </ul>
                </AlertDescription>
              </Alert>

              <div>
                <Label>Digite "EXCLUIR {selectedIds.size} PACIENTE{selectedIds.size !== 1 ? 'S' : ''}" para confirmar:</Label>
                <Input
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={`EXCLUIR ${selectedIds.size} PACIENTE${selectedIds.size !== 1 ? 'S' : ''}`}
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmationText('')}>Cancelar</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={confirmationText !== `EXCLUIR ${selectedIds.size} PACIENTE${selectedIds.size !== 1 ? 'S' : ''}` || deleting}
            >
              {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Confirmar Exclusão
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
